import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Instagram, Youtube, Mail, MapPin } from "lucide-react";
import { YOUTUBE_CHANNEL } from "../lib/episodes";
import { SOCIALS } from "../lib/logos";
import { GandabherundaMark } from "./GandabherundaMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // 'ok' | 'err' | 'loading' | null

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setStatus("err"); return; }
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("ok");
      setEmail("");
    } catch (err) {
      setStatus("err");
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative w-full border-t"
      style={{ background: "var(--dusk)", borderColor: "rgba(201,160,74,.12)" }}
    >
      <div className="border-b" style={{ borderColor: "rgba(201,160,74,.12)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-5 gap-6 items-center text-center">
          {[
            { eyebrow: "Streaming On", value: "ETV Win" },
            { eyebrow: "Broadcasting · Soon", value: "TV9" },
            { eyebrow: "Produced By", value: "Gandabherunda Arts" },
            { eyebrow: "S1 · Powered By", value: "Tamada Media" },
            { eyebrow: "S1 · Presented By", value: "Rizwan Entertainments" },
          ].map((b) => (
            <div key={b.value}>
              <div className="st-cinzel text-[0.42rem] tracking-[0.4em] uppercase text-[var(--gold-dk)]">{b.eyebrow}</div>
              <div className="mt-1 st-cormorant text-base md:text-lg text-[var(--gold-lt)]">{b.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter band */}
      <div className="border-b" style={{ borderColor: "rgba(201,160,74,.12)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="st-eyebrow">Season Two — Brewing</span>
            <h3 className="mt-3 st-cormorant text-3xl md:text-4xl italic font-light text-[var(--cream)] leading-tight">
              Be the first to know when we cross the next horizon.
            </h3>
          </div>
          <form onSubmit={submit} data-testid="footer-newsletter-form" className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="footer-newsletter-email"
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="st-input flex-1"
            />
            <button
              data-testid="footer-newsletter-submit"
              type="submit"
              className="st-btn st-btn-solid"
              disabled={status === "loading"}
            >
              {status === "loading" ? "…" : "Subscribe"}
            </button>
          </form>
          {status === "ok" && (
            <p data-testid="footer-newsletter-success" className="st-infant text-[var(--gold-lt)] md:col-start-2">
              Welcome aboard — keep your boots ready.
            </p>
          )}
          {status === "err" && (
            <p data-testid="footer-newsletter-error" className="st-infant text-[#c97070] md:col-start-2">
              Please enter a valid email.
            </p>
          )}
        </div>
      </div>

      {/* Bottom rows */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-3">
            <GandabherundaMark size={32} className="shrink-0"/>
            <div>
              <div className="st-cinzel text-[0.85rem] font-black tracking-[0.28em] text-[var(--gold)]">SOUL TRIP</div>
              <div className="st-cinzel text-[0.42rem] tracking-[0.55em] text-[var(--gold-dk)] mt-1">PRODUCED BY GANDABHERUNDA · ARTS</div>
            </div>
          </div>
          <p className="mt-5 st-infant text-[0.95rem] text-[var(--fog)] leading-relaxed max-w-xs">
            Feel the real travel. Real locations. Real conversations. Real vibe.
            <span className="block mt-2 st-cinzel text-[0.45rem] tracking-[0.35em] uppercase text-[var(--gold-dk)]">
              Now streaming on ETV Win
            </span>
          </p>
        </div>

        <div>
          <div className="st-eyebrow text-[0.5rem]">Explore</div>
          <ul className="mt-4 space-y-3">
            <li><Link data-testid="footer-link-home" to="/" className="st-cinzel text-[0.55rem] tracking-[0.3em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline">Home</Link></li>
            <li><Link data-testid="footer-link-episodes" to="/episodes" className="st-cinzel text-[0.55rem] tracking-[0.3em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline">Episodes</Link></li>
            <li><Link data-testid="footer-link-about" to="/about" className="st-cinzel text-[0.55rem] tracking-[0.3em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline">About</Link></li>
            <li><Link data-testid="footer-link-contact" to="/contact" className="st-cinzel text-[0.55rem] tracking-[0.3em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="st-eyebrow text-[0.5rem]">Reach</div>
          <ul className="mt-4 space-y-3 st-infant text-[0.95rem] text-[var(--fog)]">
            <li className="flex items-center gap-2"><Mail size={13} className="text-[var(--gold)]"/>
              <a href="mailto:gandabherundaarts@gmail.com" data-testid="footer-email" className="no-underline text-[var(--fog)] hover:text-[var(--gold)] transition-colors">gandabherundaarts@gmail.com</a>
            </li>
            <li className="flex items-center gap-2"><MapPin size={13} className="text-[var(--gold)]"/> Hyderabad · India</li>
          </ul>
        </div>

        <div>
          <div className="st-eyebrow text-[0.5rem]">Follow</div>
          <div className="mt-4 flex gap-3">
            <a data-testid="footer-social-ig" href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram · @gandabherundaarts"
               className="w-10 h-10 inline-flex items-center justify-center border border-[rgba(201,160,74,0.3)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors">
              <Instagram size={15}/>
            </a>
            <a data-testid="footer-social-yt" href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" aria-label="YouTube · @soultrip-talks"
               className="w-10 h-10 inline-flex items-center justify-center border border-[rgba(201,160,74,0.3)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors">
              <Youtube size={15}/>
            </a>
          </div>
          <div className="mt-4 space-y-2">
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ig-handle"
              className="block st-cinzel text-[0.5rem] tracking-[0.32em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline"
            >
              @gandabherundaarts
            </a>
            <a
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="block st-cinzel text-[0.5rem] tracking-[0.32em] uppercase text-[var(--fog)] hover:text-[var(--gold)] no-underline"
            >
              @soultrip-talks
            </a>
          </div>
        </div>
      </div>

      <div
        className="px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3"
        style={{ borderTop: "1px solid rgba(201,160,74,.08)" }}
      >
        <div className="st-cinzel text-[0.5rem] tracking-[0.4em] text-[var(--gold-dk)] uppercase">
          © {new Date().getFullYear()} Gandabherunda Arts · All Rights Reserved
        </div>
        <div className="st-cinzel text-[0.5rem] tracking-[0.4em] text-[var(--gold-dk)] uppercase">
          Real Locations · Real Conversations · Real Vibes
        </div>
      </div>
    </footer>
  );
};
